import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { getCalibration, interpError, interpErrorBody, listCalibration, saveCalibration } from "./interpReviewApi";
import { Button, Card, ProgressBar, ReportText, SectionTitle, Segmented, StatusPill, WorksheetTable } from "./ui";

/*
 * Blind calibration: one pair at a time. The worksheet input summary, then Report A and Report B rendered by the
 * same component. Five 1-5 rubric rows per report, a critical-error question per report, an overall preference
 * and an optional comment. Answers are saved on the server per reviewer (resume any time); which report is AI is
 * never sent to this page -- not even after all cases are submitted (only an admin sees the unblinding).
 */

const SIDES = ["A", "B"];
const SCORE_OPTS = [1, 2, 3, 4, 5].map((n) => [n, String(n)]);
const YES_NO = [[true, "Yes"], [false, "No"]];
const PREF_OPTS = [["A", "Report A"], ["B", "Report B"], ["tie", "No preference"]];

export const emptyForm = (rubric) => ({
  scores: Object.fromEntries(SIDES.map((s) => [s, Object.fromEntries((rubric || []).map((c) => [c.key, null]))])),
  critical: { A: { flag: null, note: "" }, B: { flag: null, note: "" } },
  preference: null,
  comment: "",
});

export const formFromResponse = (resp, rubric) => {
  const f = emptyForm(rubric);
  if (!resp) return f;
  for (const s of SIDES) {
    for (const c of rubric || []) f.scores[s][c.key] = resp.scores?.[s]?.[c.key] ?? null;
    f.critical[s] = { flag: resp.critical?.[s]?.flag ?? null, note: resp.critical?.[s]?.note || "" };
  }
  f.preference = resp.preference || null;
  f.comment = resp.comment || "";
  return f;
};

// The first case not yet submitted (resume point), else the first case.
export const resumeId = (items) => (items.find((i) => i.status !== "submitted") || items[0] || {}).item_id || null;

const CalibrationTab = ({ initialItem, onItemChange, toast, onProgress }) => {
  const [list, setList] = useState(null);
  const [listError, setListError] = useState(null);
  const [selectedId, setSelectedId] = useState(initialItem || null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const [missing, setMissing] = useState([]);
  const [showWorksheet, setShowWorksheet] = useState(true);

  const rubric = list?.rubric || [];
  const progress = list?.progress || { total: 0, submitted: 0, complete: false };
  const locked = Boolean(progress.complete);

  const loadList = useCallback(async () => {
    try {
      const r = await listCalibration();
      setList(r);
      setListError(null);
      if (onProgress) onProgress(r.progress);
      return r;
    } catch (e) {
      setListError(interpError(e, "Could not load the calibration cases"));
      return null;
    }
  }, [onProgress]);

  useEffect(() => {
    loadList().then((r) => {
      if (!r) return;
      setSelectedId((cur) => (cur && r.items.some((i) => i.item_id === cur) ? cur : resumeId(r.items)));
    });
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let live = true;
    setDetail(null);
    setProblem(null);
    setMissing([]);
    getCalibration(selectedId)
      .then((r) => {
        if (!live) return;
        setDetail(r);
        setForm(formFromResponse(r.response, r.rubric));
        setDirty(false);
      })
      .catch((e) => live && setProblem(interpError(e, "Could not load this case")));
    return () => {
      live = false;
    };
  }, [selectedId]);

  // Report the open item to the page (URL ?item=) through a ref, so a new callback never refetches the item.
  const onItemChangeRef = useRef(onItemChange);
  onItemChangeRef.current = onItemChange;
  useEffect(() => {
    if (selectedId && onItemChangeRef.current) onItemChangeRef.current(selectedId);
  }, [selectedId]);

  const choose = (id) => {
    if (id === selectedId) return;
    if (dirty && !window.confirm("You have unsaved answers on this case. Leave without saving?")) return;
    setSelectedId(id);
  };

  const update = (fn) => {
    setForm((f) => {
      const next = JSON.parse(JSON.stringify(f));
      fn(next);
      return next;
    });
    setDirty(true);
  };

  const save = async (submit) => {
    if (!detail || saving) return;
    setSaving(true);
    setProblem(null);
    setMissing([]);
    try {
      const r = await saveCalibration(detail.item.item_id, {
        ...form,
        submit,
        revision: detail.response ? detail.response.revision : 0,
      });
      setDetail((d) => ({ ...d, response: r.response }));
      setDirty(false);
      const fresh = await loadList();
      if (submit) {
        toast(`${detail.item.label} submitted`, "success");
        const next = fresh && fresh.items.find((i) => i.status !== "submitted");
        if (next) setSelectedId(next.item_id);
      } else {
        toast("Draft saved", "success");
      }
    } catch (e) {
      const body = interpErrorBody(e);
      if (e?.response?.status === 409 && body?.code === "CONFLICT") {
        // Saved elsewhere first: show the latest saved answers + revision (no reload, no silent overwrite).
        try {
          const r = await getCalibration(detail.item.item_id);
          setDetail(r);
          setForm(formFromResponse(r.response, r.rubric));
          setDirty(false);
          setProblem("This case was changed on another tab or device. The latest saved answers are shown; "
            + "your changes were not saved. Re-enter them and save again.");
          loadList();
        } catch (e2) {
          setProblem(interpError(e2, "This case was changed elsewhere and could not be reloaded"));
        }
        return;
      }
      setProblem(interpError(e, "Could not save"));
      if (Array.isArray(body?.detail)) setMissing(body.detail);
      if (body?.code === "CALIBRATION_LOCKED") loadList();
    } finally {
      setSaving(false);
    }
  };

  const miss = useMemo(() => new Set(missing), [missing]);

  if (listError) return <Card className="p-6 text-[13px] text-red-700">{listError}</Card>;
  if (!list) return <div className="flex justify-center py-16"><CircularProgress size={28} /></div>;
  if (!list.items.length) return <Card className="p-6 text-[13px] text-[#6B7280]">No calibration cases are loaded yet.</Card>;

  const item = detail?.item;
  const nav = detail?.nav;

  return (
    <div className="flex flex-col gap-4">
      <Card className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-[13px] font-semibold text-[#1D2951]">Submitted</span>
          <ProgressBar done={progress.submitted} total={progress.total} label="Calibration progress" />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="case-strip">
          {list.items.map((i) => (
            <button key={i.item_id} type="button" onClick={() => choose(i.item_id)} title={`${i.label} - ${i.status}`}
              aria-current={i.item_id === selectedId ? "true" : undefined}
              className={`w-10 h-10 rounded-lg border text-[12px] font-semibold ${
                i.item_id === selectedId ? "ring-2 ring-[#1D2951] " : ""}${
                i.status === "submitted" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                  : i.status === "draft" ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-white border-[#D7DCE3] text-[#1D2951]"}`}>
              {i.seq}
            </button>
          ))}
        </div>
      </Card>

      {locked && (
        <Card className="px-4 py-3 text-[13px] text-emerald-900 bg-emerald-50 border-emerald-200" data-testid="calibration-done">
          All {progress.total} cases are submitted. Thank you — your answers are saved and now locked.
        </Card>
      )}

      {!detail || !form ? (
        problem ? <Card className="p-6 text-[13px] text-red-700">{problem}</Card>
          : <div className="flex justify-center py-16"><CircularProgress size={28} /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-[#18181B]">{item.label}</h2>
              <span className="text-[13px] text-[#6B7280]">{item.study_type}</span>
              <StatusPill status={detail.response?.status || "new"} />
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={!nav?.prev_id} onClick={() => choose(nav.prev_id)}>Previous</Button>
              <Button variant="secondary" disabled={!nav?.next_id} onClick={() => choose(nav.next_id)}>Next</Button>
            </div>
          </div>

          <Card>
            <SectionTitle right={
              <button type="button" className="text-[13px] text-[#1D2951] underline h-10 px-2"
                onClick={() => setShowWorksheet((v) => !v)}>{showWorksheet ? "Hide" : "Show"}</button>}>
              Worksheet input summary
            </SectionTitle>
            {showWorksheet && <div className="pb-3"><WorksheetTable rows={item.worksheet} /></div>}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {SIDES.map((s) => (
              <Card key={s}>
                <SectionTitle>Report {s}</SectionTitle>
                <div className="px-4 pb-4">
                  <ReportText text={s === "A" ? item.report_a : item.report_b} testid={`report-${s}`} />
                </div>
              </Card>
            ))}
          </div>

          <Card>
            <SectionTitle>Rubric (1 = poor, 5 = excellent)</SectionTitle>
            <div className="px-4 pb-4 overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[#6B7280]">
                    <th className="py-2 pr-3 font-medium">Criterion</th>
                    {SIDES.map((s) => <th key={s} className="py-2 pr-3 font-medium">Report {s}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rubric.map((c) => (
                    <tr key={c.key} className="border-t border-[#F1F2F4] align-top">
                      <td className="py-2 pr-3 min-w-[180px]">
                        <div className="font-semibold text-[#18181B]">{c.label}</div>
                        <div className="text-[12px] text-[#6B7280]">{c.help}</div>
                      </td>
                      {SIDES.map((s) => (
                        <td key={s} className={`py-2 pr-3 ${miss.has(`${s}.${c.key}`) ? "bg-red-50" : ""}`}>
                          <Segmented options={SCORE_OPTS} value={form.scores[s][c.key]} disabled={locked}
                            label={`${c.label} Report ${s}`} testid={`score-${s}-${c.key}`}
                            onChange={(v) => update((f) => { f.scores[s][c.key] = v; })} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {SIDES.map((s) => (
              <Card key={s} className={miss.has(`critical.${s}`) || miss.has(`critical.${s}.note`) ? "border-red-300" : ""}>
                <SectionTitle>Critical error in Report {s}?</SectionTitle>
                <div className="px-4 pb-4 flex flex-col gap-2">
                  <p className="text-[12px] text-[#6B7280]">An error that could change management or harm the patient.</p>
                  <Segmented options={YES_NO} value={form.critical[s].flag} disabled={locked}
                    label={`Critical error in Report ${s}`} testid={`critical-${s}`}
                    onChange={(v) => update((f) => { f.critical[s].flag = v; })} />
                  {form.critical[s].flag === true && (
                    <textarea aria-label={`Describe the critical error in Report ${s}`} disabled={locked}
                      className="w-full min-h-[72px] rounded-lg border border-[#D7DCE3] p-2 text-[14px]"
                      placeholder="What is the error?" value={form.critical[s].note}
                      onChange={(e) => { const v = e.target.value; update((f) => { f.critical[s].note = v; }); }} />
                  )}
                </div>
              </Card>
            ))}
          </div>

          <Card className={miss.has("preference") ? "border-red-300" : ""}>
            <SectionTitle>Which report would you rather sign?</SectionTitle>
            <div className="px-4 pb-4 flex flex-col gap-3">
              <Segmented options={PREF_OPTS} value={form.preference} disabled={locked} label="Overall preference"
                testid="preference" onChange={(v) => update((f) => { f.preference = v; })} />
              <textarea aria-label="Comment (optional)" disabled={locked}
                className="w-full min-h-[72px] rounded-lg border border-[#D7DCE3] p-2 text-[14px]"
                placeholder="Comment (optional)" value={form.comment}
                onChange={(e) => { const v = e.target.value; update((f) => { f.comment = v; }); }} />
            </div>
          </Card>

          {problem && <div className="text-[13px] text-red-700" role="alert">{problem}</div>}
          {!locked && (
            <div className="flex flex-wrap justify-end gap-2 pb-6">
              <Button variant="secondary" disabled={saving} onClick={() => save(false)}>Save draft</Button>
              <Button disabled={saving} onClick={() => save(true)}>
                {detail.response?.status === "submitted" ? "Update submitted answers" : "Submit & next"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CalibrationTab;
